"use client";

import { usePathname } from "next/navigation";
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";

import { getOrCreateHomeEntryAttribution } from "@/utils/attribution";
import type { EventParams } from "@/utils/analytics";
import { trackEvent } from "@/utils/analytics";
import { cn } from "@/utils/cn";
import { ensureTrialSessionStarted, getOrCreateTrialSessionId, trackTrialEvent } from "@/utils/trialTracking";

const TRIAL_EXTERNAL_BASE = "https://searchright-trial-hw.vercel.app";
const TRIAL_EXTRACT_PATH = "/extract";
const URL_RE = /^https?:\/\/\S+$/i;
const MIN_TEXT = 30;
const MIN_URL = 10;
const TEXTAREA_MIN_HEIGHT = 64;
const TEXTAREA_MAX_HEIGHT = 320;

type Sample = {
  label: string;
  body: string;
};

const SAMPLES: Sample[] = [
  {
    label: "백엔드 시니어",
    body: `우리는 B2B SaaS 결제 플랫폼을 만드는 시리즈 C 스타트업입니다. 백엔드 시니어 엔지니어를 찾고 있습니다.

담당 업무
- 결제 처리 시스템의 안정성 및 확장성 강화
- MSA 환경에서 결제, 정산, 회계 도메인 서비스 설계 및 개발
- 트래픽 폭주 상황에서의 시스템 최적화

자격 요건
- 5년 이상의 백엔드 개발 경력
- Java/Kotlin 및 Spring 기반 서비스 운영 경험
- MySQL/PostgreSQL 및 Redis 운영 경험
- AWS 환경에서의 운영 경험

우대 사항
- 핀테크/결제 도메인 경험
- Kafka 등 메시지 큐 시스템 경험
- 대규모 트래픽 처리 경험`,
  },
  {
    label: "프론트엔드 미들",
    body: `B2C 커머스 플랫폼의 프론트엔드 엔지니어를 찾고 있습니다.

담당 업무
- React/Next.js 기반 메인 서비스 UI 개발
- 결제·주문 페이지 성능 최적화
- 디자인 시스템 구축 및 운영
- A/B 테스트 인프라 정비

자격 요건
- 3~6년 프론트엔드 개발 경력
- TypeScript + React/Next.js 운영 경험
- 상태 관리 라이브러리 운영 경험 (Redux/Zustand/Recoil 등)

우대 사항
- 이커머스 도메인 경험
- 디자인 시스템 0→1 경험
- Web Vitals 등 성능 최적화 경험`,
  },
  {
    label: "PM 시니어",
    body: `B2B SaaS 제품의 시니어 프로덕트 매니저를 찾고 있습니다.

담당 업무
- 고객사 인터뷰 및 요구사항 정리
- 제품 로드맵 수립 및 우선순위 결정
- 엔지니어링·디자인 팀과 협업 리드
- 데이터 기반 의사결정 프로세스 정립

자격 요건
- B2B SaaS 제품 PM 5년 이상
- 고객 인터뷰 및 시장 분석 경험
- 영어 비즈니스 커뮤니케이션 가능

우대 사항
- 글로벌 시장 진출 경험
- SQL 등 데이터 분석 도구 활용 능력
- Enterprise 영업 협업 경험`,
  },
  {
    label: "디자이너 미들",
    body: `AI 기반 생산성 도구를 만드는 스타트업의 프로덕트 디자이너를 찾고 있습니다.

담당 업무
- AI 워크플로 UI/UX 설계
- 디자인 시스템 구축 및 컴포넌트 디자인
- 사용자 리서치 결과를 디자인에 반영
- PM·엔지니어와 긴밀한 협업

자격 요건
- 3~6년 프로덕트 디자인 경력
- Figma 기반 디자인 시스템 운영 경험
- 모바일·웹 양쪽 디자인 경험

우대 사항
- AI/LLM 기반 제품 디자인 경험
- 사용자 인터뷰 직접 수행 경험
- 마이크로 인터랙션 디자인 능숙`,
  },
];

type TrialHeroVariantProps = {
  experimentParams: EventParams;
};

type TrackTrialOptions = {
  mirrorGa4?: boolean;
};

export default function TrialHeroVariant({ experimentParams }: TrialHeroVariantProps) {
  const path = usePathname() ?? "/";
  const [jd, setJd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const inputStartedRef = useRef(false);
  const urlDetectedRef = useRef(false);
  const mountFiredRef = useRef(false);

  const trimmed = jd.trim();
  const isUrl = URL_RE.test(trimmed);
  const valid = isUrl ? trimmed.length >= MIN_URL : trimmed.length >= MIN_TEXT;
  const attributionContext = getOrCreateHomeEntryAttribution();

  const trackTrial = (eventName: string, params?: EventParams, { mirrorGa4 = true }: TrackTrialOptions = {}) => {
    const data = { ...attributionContext, ...experimentParams, ...params };
    if (mirrorGa4) {
      trackEvent(eventName, path, data);
    }
    trackTrialEvent(eventName, data);
  };

  useEffect(() => {
    if (mountFiredRef.current) return;
    mountFiredRef.current = true;
    ensureTrialSessionStarted(experimentParams);
    trackTrial("page_view", { route: "/" }, { mirrorGa4: false });
    trackTrial("home_viewed", undefined, { mirrorGa4: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(Math.max(ta.scrollHeight, TEXTAREA_MIN_HEIGHT), TEXTAREA_MAX_HEIGHT)}px`;
  }, [jd]);

  const submitJd = () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    const sessionId = getOrCreateTrialSessionId();
    const params = new URLSearchParams({ sid: sessionId });
    params.set(isUrl ? "jdUrl" : "jd", trimmed);
    const destination = `${TRIAL_EXTERNAL_BASE}${TRIAL_EXTRACT_PATH}?${params.toString()}`;

    if (!isUrl) {
      trackTrial("jd_submitted", { jdText: trimmed, method: "text", length: trimmed.length, destination });
    }
    window.location.href = destination;
  };

  const pickSample = (sample: Sample) => {
    setJd(sample.body);
    trackTrial("sample_jd_selected", { label: sample.label });
    requestAnimationFrame(() => taRef.current?.focus());
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      submitJd();
    }
  };

  return (
    <section className="trial-surface flex h-auto min-h-[620px] w-full flex-col items-center justify-center px-side py-10 desktop:h-[calc(100vh-4rem)] desktop:min-h-[calc(100vh-4rem)] desktop:py-0">
      <div className="w-full max-w-2xl">
        <div className="mb-5 flex justify-center tablet:mb-6">
          <span className="trial-glass-pill inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--trial-brand)]">
            <span className="trial-brand-gradient inline-flex h-4 w-4 items-center justify-center rounded-full text-white">
              <svg
                width="9"
                height="9"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            AI 리크루터가 직접 발굴합니다
          </span>
        </div>
        <h1 className="mb-10 text-center text-[40px] font-bold leading-[1.08] tracking-[-0.025em] text-[var(--trial-foreground)] tablet:mb-12 tablet:text-[56px]">
          채용 공고로
          <br />
          만날 수 없는 <span className="trial-brand-gradient-text">핵심 인재</span>
          <br />
          1주 안에 커피챗까지
        </h1>

        <div
          className={cn(
            "trial-glass-strong rounded-[28px] p-2 transition-shadow duration-200",
            valid && "ring-2 ring-[rgba(79,109,245,0.22)]",
          )}
        >
          <div className="relative">
            <textarea
              ref={taRef}
              value={jd}
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                const next = event.target.value;
                setJd(next);
                if (!inputStartedRef.current && next.length > 0) {
                  inputStartedRef.current = true;
                  trackTrial("jd_input_started");
                }
                const looksUrl = URL_RE.test(next.trim());
                if (looksUrl && !urlDetectedRef.current) {
                  urlDetectedRef.current = true;
                  trackTrial("jd_url_detected", { url: next.trim() });
                } else if (!looksUrl && urlDetectedRef.current) {
                  urlDetectedRef.current = false;
                }
              }}
              onKeyDown={onKeyDown}
              placeholder="어떤 인재를 찾으세요? JD 링크나 본문을 알려주세요"
              rows={1}
              style={{ minHeight: "56px" }}
              className="w-full resize-none bg-transparent px-5 py-4 pr-16 text-[15.5px] leading-relaxed text-[var(--trial-foreground)] placeholder:text-[rgba(82,90,115,0.7)] focus:outline-none tablet:text-[17px]"
              autoFocus
            />
            <button
              type="button"
              onClick={submitJd}
              disabled={!valid || submitting}
              aria-label={submitting ? "체험 이동 중" : "평가 요건 추출"}
              className="trial-brand-gradient absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-white shadow-[0_4px_16px_-2px_rgba(79,109,245,0.5)] transition-all duration-200 enabled:hover:scale-[1.04] enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-30"
            >
              {submitting ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {SAMPLES.map((sample) => (
            <button
              key={sample.label}
              type="button"
              onClick={() => pickSample(sample)}
              className="trial-glass-subtle trial-lift rounded-full px-3.5 py-1.5 text-[12.5px] text-[var(--trial-muted-strong)] transition-colors hover:text-[var(--trial-foreground)]"
            >
              {sample.label}
            </button>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-[12px] text-[var(--trial-muted)]">
          <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--trial-success)]" />
          <span>
            이번 주 <strong className="font-mono text-[var(--trial-foreground)]">12</strong>개 팀이 트라이얼 시작
            <span className="mx-1 opacity-40">·</span>
            평균 <strong className="font-mono text-[var(--trial-foreground)]">23</strong>초 만에 후보 3명 발굴
          </span>
          <span className="hidden text-[rgba(82,90,115,0.5)] tablet:inline">(체험판 누적)</span>
        </div>
      </div>
    </section>
  );
}
